import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../services/api';
import Button from '../components/ui/Button';
import ProgressBar from '../components/ProgressBar';
import Navigation from '../components/Navigation';
import { CheckCircle, Mail, AlertCircle, RefreshCw, Copy, ExternalLink } from 'lucide-react';
import Footer from '../components/Footer';
import { DEFAULT_PRICING, formatPrice } from './LanguageSelectionPage';

const FPS_PHONE_NUMBER = '+852 5177 6613';

const StartSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get data from location state
  const { 
    classType, 
    userEmail, 
    userName, 
    classTypeId, 
    availabilityArray,
    emailStatus: initialEmailStatus,
    isTutoringTrial
  } = location.state || {};

  const [emailStatus, setEmailStatus] = useState(initialEmailStatus || 'completed');
  const [emailError, setEmailError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [pricing, setPricing] = useState(DEFAULT_PRICING);

  // Send emails in the background if status is 'sending'
  useEffect(() => {
    if (emailStatus === 'sending' && userEmail && userName && availabilityArray && classTypeId) {
      sendConfirmationEmails();
    }
  }, [emailStatus]);

  // Load current pricing so the amount due reflects any admin-configured changes
  useEffect(() => {
    const loadPricing = async () => {
      try {
        const settings = await api.admin.getSettings();
        if (settings?.pricing) {
          const parsed = typeof settings.pricing === 'string'
            ? JSON.parse(settings.pricing)
            : settings.pricing;
          setPricing({
            private: {
              ...DEFAULT_PRICING.private,
              trial: { ...DEFAULT_PRICING.private.trial, ...(parsed.private?.trial || {}) },
            },
            group: {
              starter: { ...DEFAULT_PRICING.group.starter, ...(parsed.group?.starter || {}) },
            },
          });
        }
      } catch (err) {
        console.error('Error loading pricing, using defaults:', err);
      }
    };

    loadPricing();
  }, []);

  // Amount due depends on whether the student booked a private tutoring trial or a group class
  const amountDue = isTutoringTrial ? pricing.private.trial.price : pricing.group.starter.price;

  // Human-readable summary of the class the student requested, e.g. "Mandarin Group Class"
  const languageLabel = classType === 'mandarin' ? 'Mandarin' : classType === 'cantonese' ? 'Cantonese' : null;
  const classSummary = languageLabel
    ? `${languageLabel} ${isTutoringTrial ? 'Private Tutoring Trial' : 'Group Class'}`
    : null;

  const copyFpsNumberToClipboard = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(FPS_PHONE_NUMBER.replace(/\s/g, ''));
    }
  };

  const sendConfirmationEmails = async () => {
    try {
      await api.notifications.sendAvailabilityConfirmation(
        userName,
        userEmail,
        classType,
        availabilityArray,
        classTypeId,
        null, // phone
        null, // goalData
        isTutoringTrial  // Include flag for email template
      );
      
      setEmailStatus('sent');
      setEmailError(null);
    } catch (error) {
      console.warn('Failed to send confirmation email:', error);
      setEmailStatus('failed');
      setEmailError(error.message || 'Failed to send confirmation email');
    }
  };

  const retryEmailSending = async () => {
    setEmailStatus('sending');
    setEmailError(null);
    setRetryCount(prev => prev + 1);
    await sendConfirmationEmails();
  };

  const copyEmailToClipboard = () => {
    if (userEmail && navigator.clipboard) {
      navigator.clipboard.writeText(userEmail);
    }
  };

  const getEmailStatusContent = () => {
    switch (emailStatus) {
      case 'sending':
        return (
          <div className="flex items-center text-blue-400">
            <RefreshCw size={16} className="mr-2 animate-spin" />
            <span>Sending confirmation email to {userEmail}...</span>
          </div>
        );

      case 'sent':
      case 'completed':
        return (
          <div className="space-y-2">
            <div className="flex items-center text-green-400">
              <Mail size={16} className="mr-2 mt-0.5" />
              <span>Confirmation email sent</span>
            </div>
            <p className="text-sm text-gray-400">
              Check your inbox at {userEmail}. If you don't see it, check your spam folder.
            </p>
          </div>
        );

      case 'failed':
        return (
          <div className="space-y-4">
            <div className="flex items-start text-yellow-400">
              <AlertCircle size={16} className="mr-2 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">Email delivery issue</p>
                <p className="text-sm text-yellow-300 mt-1">
                  We couldn't send the confirmation email to {userEmail}, but your availability has been saved successfully.
                </p>
                {emailError && (
                  <p className="text-xs text-gray-400 mt-2 font-mono">
                    Error: {emailError}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                size="small"
                onClick={retryEmailSending}
                disabled={retryCount >= 3}
                className="border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:bg-opacity-10 flex items-center justify-center"
              >
                <RefreshCw size={14} className="mr-2" />
                {retryCount >= 3 ? 'Max Retries Reached' : `Retry Email ${retryCount > 0 ? `(${retryCount}/3)` : ''}`}
              </Button>
              
              <Button
                variant="ghost"
                size="small"
                onClick={copyEmailToClipboard}
                className="text-gray-400 hover:text-white flex items-center justify-center"
              >
                <Copy size={14} className="mr-2" />
                Copy Email Address
              </Button>
            </div>

            <div className="bg-blue-900 bg-opacity-20 border border-blue-700 rounded p-3">
              <p className="text-blue-300 text-sm">
                <strong>Don't worry!</strong> Your availability has been saved. 
                {isTutoringTrial 
                  ? " We'll contact you to schedule your trial lesson, even without the confirmation email."
                  : " We'll contact you when a class forms, even without the confirmation email."
                }
              </p>
            </div>
          </div>
        );

      default:
        return (
          <div className="flex items-center text-green-400">
            <CheckCircle size={16} className="mr-2" />
            <span>All confirmations completed</span>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />
      <main className="container mx-auto py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <ProgressBar currentStep={3} />
          
          {/* Success animation and message */}
          <div className="text-center mb-8">
            <div className="mb-6 flex justify-center">
              <div className="relative">
                <CheckCircle size={48} className="text-green-400" />
                <div className="absolute inset-0 animate-ping">
                  <CheckCircle size={48} className="text-green-400 opacity-20" />
                </div>
              </div>
            </div>
            
            <h2 className="text-3xl font-bold mb-2">Availability Submitted!</h2>
            <div className="w-16 h-1 bg-green-500 mx-auto mt-2"></div>
            {classSummary && (
              <p className="text-purple-300 font-medium mt-4">{classSummary}</p>
            )}
            <p className="text-gray-300 mt-2">
              {isTutoringTrial
                ? "We'll get back to you to confirm your trial lesson time."
                : "We'll get back to you on confirming one of the times that you're available."
              }
            </p>
          </div>

          {/* Email Status Section */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden mb-8">
            <div className="p-6">
              {getEmailStatusContent()}
            </div>
          </div>

          {/* What happens next */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-4 border-b border-gray-700">
              <h3 className="font-semibold text-lg">What Happens Next?</h3>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                {isTutoringTrial ? (
                  // Tutoring trial flow
                  <>
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4 mt-1">
                        1
                      </div>
                      <div>
                        <h4 className="font-medium">Review Your Availability</h4>
                        <p className="text-gray-400 text-sm">We'll review the times you've provided and check our tutors' schedules.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4 mt-1">
                        2
                      </div>
                      <div>
                        <h4 className="font-medium">Confirm Your Trial Lesson</h4>
                        <p className="text-gray-400 text-sm">We'll contact you to confirm a specific time that works for both you and your tutor.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4 mt-1">
                        3
                      </div>
                      <div>
                        <h4 className="font-medium">Enjoy Your Trial Lesson</h4>
                        <p className="text-gray-400 text-sm">Join your trial lesson at the scheduled time and meet your tutor.</p>
                      </div>
                    </div>
                  </>
                ) : (
                  // Group class flow
                  <>
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4 mt-1">
                        1
                      </div>
                      <div>
                        <h4 className="font-medium">Review Your Availability</h4>
                        <p className="text-gray-400 text-sm">We'll review the times you've provided and check our schedule.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4 mt-1">
                        2
                      </div>
                      <div>
                        <h4 className="font-medium">Confirm Your Time</h4>
                        <p className="text-gray-400 text-sm">We'll get back to you with a confirmed time that works with your availability.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4 mt-1">
                        3
                      </div>
                      <div>
                        <h4 className="font-medium">Start Your Course</h4>
                        <p className="text-gray-400 text-sm">Once confirmed, you'll receive all the details to begin your course.</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Payment Section */}
          <div className="mb-8">
            <h3 className="text-center text-xl font-semibold mb-6">Pay Now & Save Time</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start relative">
              {/* Pay via FPS Card */}
              <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-4 border-b border-gray-700">
                  <h4 className="font-semibold text-lg flex items-center">
                    <span className="bg-white rounded p-1 mr-2 flex items-center justify-center flex-shrink-0">
                      <img src="/fps-logo.svg" alt="FPS" className="w-5 h-5" />
                    </span>
                    Pay via FPS
                  </h4>
                </div>

                <div className="p-6">
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start text-sm text-gray-400">
                      <CheckCircle size={16} className="mr-2 mt-0.5 text-green-400 flex-shrink-0" />
                      <span>Instant, fee-free transfer</span>
                    </li>
                  </ul>

                  <div className="bg-gray-900 rounded-lg border border-gray-700 p-4 mb-4">
                    <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-800">
                      <div>
                        <span className="text-sm text-gray-400 block">Amount due</span>
                        {classSummary && (
                          <span className="text-xs text-gray-500">{classSummary}</span>
                        )}
                      </div>
                      <span className="text-white font-bold text-lg">HKD {formatPrice(amountDue)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <span className="text-xs text-gray-400 block">FPS phone number</span>
                        <span className="text-white font-semibold">{FPS_PHONE_NUMBER}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="small"
                        onClick={copyFpsNumberToClipboard}
                        className="text-gray-400 hover:text-white flex items-center justify-center flex-shrink-0"
                      >
                        <Copy size={14} className="mr-1" />
                        Copy
                      </Button>
                    </div>
                  </div>

                  <p className="text-sm text-gray-400 leading-relaxed">
                    Open your banking app, choose FPS transfer to a phone number, and send{' '}
                    <span className="text-gray-300 font-medium">HKD {formatPrice(amountDue)}</span> to{' '}
                    <span className="text-gray-300 font-medium">{FPS_PHONE_NUMBER}</span>. Please include your name as the payment reference.
                  </p>
                </div>
              </div>

              {/* OR Divider */}
              <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-black px-3 py-1 rounded-full border border-gray-700">
                <span className="text-gray-500 font-bold text-sm">OR</span>
              </div>

              {/* Mobile OR Divider */}
              <div className="md:hidden flex items-center justify-center">
                <div className="bg-black px-3 py-1 rounded-full border border-gray-700">
                  <span className="text-gray-500 font-bold text-sm">OR</span>
                </div>
              </div>

              {/* Pay In Person Card */}
              <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-4 border-b border-gray-700">
                  <h4 className="font-semibold text-lg flex items-center">
                    <span className="mr-2">🏪</span>
                    Pay In Person Later
                  </h4>
                </div>
                
                <div className="p-6">
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start text-sm text-gray-400">
                      <CheckCircle size={16} className="mr-2 mt-0.5 text-green-400 flex-shrink-0" />
                      <span>Pay when you arrive</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-400">
                      <CheckCircle size={16} className="mr-2 mt-0.5 text-green-400 flex-shrink-0" />
                      <span>Cash or card accepted</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-400">
                      <CheckCircle size={16} className="mr-2 mt-0.5 text-green-400 flex-shrink-0" />
                      <span>No rush needed</span>
                    </li>
                  </ul>
                  
                  <p className="text-sm text-gray-400 leading-relaxed">
                    We'll remind you about payment when we confirm your class time.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Actions */}
          <div className="text-center space-y-4">
            {emailStatus === 'failed' && (
              <p className="text-gray-400 text-sm">
                Need help? Contact us at{' '}
                <a 
                  href="mailto:aileen@mandarintutorhk.com" 
                  className="text-purple-400 hover:text-purple-300"
                >
                  aileen@mandarintutorhk.com
                </a>
              </p>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default StartSuccessPage;